import type { EventAlignment, EventDto, EventKind } from '@odo/shared-ui';
import apiV2 from '../api/v2';
import type { PaginatedMeta } from './accounts';

// Events admin (admin-only /api/v2/events). The banner/modal DTO (`EventDto`)
// lives in `@odo/shared-ui`; this file adds only the admin-only wire calls.

/** `GET /events/:eventId` - the DTO plus audit fields, admin-only. */
export interface EventAdminDetail extends EventDto {
    createdAt: string;
    updatedAt: string;
    createdByUserId: string | null;
    updatedByUserId: string | null;
    /** Live sign-ups, counted server-side for this event. */
    signupCount: number;
}

// `zoomUrl` never comes back from the API (spec §7) - it only ever appears
// here, on the way IN, on create/update.
export interface CreateEventInput {
    // No `id`: the server derives the slug from the title, and it is immutable
    // afterwards because sign-ups reference it.
    enabled?: boolean;
    kind: EventKind;
    startsAt: string;
    /** Hours. The server derives endsAt and showUntil from this. */
    durationHours: number;
    showFrom: string;
    title: string;
    topic: string;
    badgeLabel: string;
    badgeTag?: string | null;
    subtitle: string;
    /** Pop-up title override; null inherits `title`. */
    modalTitle?: string | null;
    /** Rich pop-up subtitle override; null inherits `subtitle`. */
    modalSubtitleDoc?: unknown;
    /** False hides the pop-up's subtitle block entirely. */
    modalShowSubtitle?: boolean;
    body?: string | null;
    bodyDoc?: unknown;
    /** Practice ids allowed to see this event. EMPTY MEANS EVERY PRACTICE.
     *  Omitted on PATCH leaves the stored audience untouched; `[]` resets it
     *  to everyone - the same omit/clear semantics as every other field. */
    practices?: string[];
    themePreset: string;
    /** Banner layout; server defaults to 'left' on create if omitted. */
    alignment?: EventAlignment;
    iconKey?: string | null;
    actionLabel: string;
    countdownToEvent?: boolean;
    /** Meetings only - an announcement has no meeting link. */
    zoomUrl?: string;
    googleCalendarUrl?: string | null;
    icsUrl?: string | null;
}

/** Every authored field is patchable; the slug never is. */
export type UpdateEventInput = Partial<CreateEventInput>;

/** A row of `GET /events`. The list endpoint resolves `signupCount` in one
 *  grouped query - only on the list shape, the single-event GET leaves it at 0. */
export interface EventListRow extends EventDto {
    signupCount: number;
}

/** A row of `GET /events/:eventId/signups`, admin-only. */
export interface EventSignupRow {
    userId: string;
    email: string;
    fullname: string | null;
    status: string;
    signedUpAt: string;
    cancelledAt: string | null;
}

export interface EventsListParams {
    page: number;
    limit: number;
}

const BASE_PATH = '/events';

export async function fetchEvents(
    params: EventsListParams
): Promise<{ data: EventListRow[]; meta: PaginatedMeta }> {
    const response = await apiV2.get<{ success: true; data: EventListRow[]; meta: PaginatedMeta }>(
        BASE_PATH,
        { params }
    );
    return { data: response.data.data, meta: response.data.meta };
}

export interface EventSignupsParams {
    page: number;
    limit: number;
}

/** Paginated the same way `GET /events` is (page/limit in, meta.total out). */
export async function fetchEventSignups(
    eventId: string,
    params: EventSignupsParams
): Promise<{ data: EventSignupRow[]; meta: PaginatedMeta }> {
    const response = await apiV2.get<{
        success: true;
        data: EventSignupRow[];
        meta: PaginatedMeta;
    }>(`${BASE_PATH}/${eventId}/signups`, { params });
    return { data: response.data.data, meta: response.data.meta };
}

export async function fetchEvent(id: string): Promise<EventAdminDetail> {
    const response = await apiV2.get<{ success: true; data: EventAdminDetail }>(
        `${BASE_PATH}/${id}`
    );
    return response.data.data;
}

export async function createEvent(input: CreateEventInput): Promise<EventAdminDetail> {
    const response = await apiV2.post<{ success: true; data: EventAdminDetail }>(BASE_PATH, input);
    return response.data.data;
}

export async function updateEvent(id: string, patch: UpdateEventInput): Promise<EventAdminDetail> {
    const response = await apiV2.patch<{ success: true; data: EventAdminDetail }>(
        `${BASE_PATH}/${id}`,
        patch
    );
    return response.data.data;
}

export async function deleteEvent(id: string): Promise<void> {
    await apiV2.delete(`${BASE_PATH}/${id}`);
}

/** What `POST /events/preview-email` composes from an unsaved draft. */
export interface EventEmailPreviewInput {
    kind: EventKind;
    title: string;
    topic: string;
    badgeLabel: string;
    subtitle: string;
    body: string | null;
    bodyDoc: unknown;
    themePreset: string;
    startsAt: string;
    googleCalendarUrl: string | null;
    icsUrl: string | null;
}

/** The confirmation email as HTML, rendered by the SAME server-side template that
 *  sends it - not a client-side reimplementation, which would drift. */
export async function previewEventEmail(
    input: EventEmailPreviewInput
): Promise<{ subject: string; html: string }> {
    const response = await apiV2.post<{
        success: true;
        data: { subject: string; html: string };
    }>(`${BASE_PATH}/preview-email`, input);
    return response.data.data;
}
